import { useEffect, useState } from "react";
import { fetchCaveFronts, type CaveFront } from "../api";
import CaveLobbyEditor from "../CaveLobbyEditor";

/** The admin Cave Designer tab (ADR 0186 Phase 2). A single-front backend needs no
 *  picker — it authors the default front (id == backend id). With multiple Caves
 *  (fronts) on one backend, a front selector lets the admin author each Cave's own
 *  branded lobby; the editor re-loads for the chosen front (the lobby is front-keyed,
 *  while assets/tickets/legend stay backend-shared). */
export default function LobbyDesignerTab({
  caveId,
  caveName,
  canPublish,
}: {
  caveId: string;
  caveName: string;
  canPublish: boolean;
}) {
  const [fronts, setFronts] = useState<CaveFront[]>([]);
  const [activeFront, setActiveFront] = useState<string>(caveId); // default front id == backend id

  useEffect(() => {
    let live = true;
    fetchCaveFronts(caveId)
      .then((fs) => {
        if (live) setFronts(fs.filter((f) => !f.archived));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [caveId]);

  const multi = fronts.length > 1;
  const active = fronts.find((f) => f.id === activeFront);
  // The default front's lobby is keyed by the backend id, so pass frontId=undefined for
  // it (the server defaults to the default front) and the explicit id otherwise.
  const frontId = active && !active.default ? active.id : undefined;
  const displayName = active?.name ?? caveName;

  return (
    <div>
      {multi && (
        <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <label htmlFor="designer-front" style={{ fontSize: "var(--fs-2xs)", color: "var(--text-dim)" }}>
            Cave (storefront)
          </label>
          <select
            id="designer-front"
            aria-label="Cave to design"
            value={activeFront}
            onChange={(e) => setActiveFront(e.target.value)}
          >
            {fronts.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
                {f.default ? " (default)" : ""}
              </option>
            ))}
          </select>
        </div>
      )}
      <CaveLobbyEditor key={activeFront} caveId={caveId} caveName={displayName} frontId={frontId} canPublish={canPublish} />
    </div>
  );
}
